import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock, Loader2 } from "lucide-react";
import { API, axios } from "@/lib/api";
import { fadeUp, stagger, viewport } from "@/lib/motion";
import { useSEO } from "@/lib/seo";

const BlogList = () => {
  const [posts, setPosts] = useState(null);
  useSEO({
    title: "Blog | BrandToBuilt — Small business growth, websites & SEO tips",
    description: "Plain-English guides on websites, branding, SEO and getting found online — written for busy UK business owners, not techies.",
    path: "/blog",
  });

  useEffect(() => {
    axios.get(`${API}/posts`).then(({ data }) => setPosts(data)).catch(() => setPosts([]));
  }, []);

  const featured = posts?.[0];
  const rest = posts?.slice(1) || [];

  return (
    <div className="pt-28 md:pt-36 pb-24 min-h-screen" data-testid="blog-list">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <motion.div variants={fadeUp} initial="hidden" animate="show" className="mb-14 max-w-3xl">
          <p className="text-[#ccff00] text-xs font-semibold uppercase tracking-widest mb-4">The BrandToBuilt blog</p>
          <h1 className="font-display font-black tracking-tighter text-5xl md:text-6xl lg:text-7xl leading-none mb-6">
            Grow your <span className="text-[#ccff00]">small business.</span>
          </h1>
          <p className="text-lg text-zinc-400">
            Plain-English guides on websites, branding, SEO and getting found online — written for busy UK business owners, not techies.
          </p>
        </motion.div>

        {posts === null && (
          <div className="flex items-center justify-center py-20 text-zinc-500" data-testid="blog-loading"><Loader2 className="animate-spin mr-2" /> Loading articles…</div>
        )}

        {posts && posts.length === 0 && (
          <p className="text-zinc-500 py-20" data-testid="blog-empty">No articles yet — check back soon.</p>
        )}

        {featured && (
          <Link to={`/blog/${featured.slug}`} className="group grid md:grid-cols-2 gap-8 rounded-2xl overflow-hidden bg-[#121212] border border-white/10 hover:border-[#ccff00]/40 transition-colors mb-12" data-testid="blog-featured">
            <div className="h-64 md:h-full overflow-hidden">
              <img src={featured.cover_image} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="p-8 md:p-10 flex flex-col justify-center">
              <div className="flex items-center gap-3 text-xs text-zinc-500 mb-4">
                <span className="text-[#ccff00] uppercase tracking-widest font-semibold">{featured.category}</span>
                <span className="flex items-center gap-1"><Clock size={12} /> {featured.read_time} min read</span>
              </div>
              <h2 className="font-display font-black text-3xl md:text-4xl leading-tight mb-4 group-hover:text-[#ccff00] transition-colors">{featured.title}</h2>
              <p className="text-zinc-400 mb-6">{featured.excerpt}</p>
              <span className="inline-flex items-center gap-2 text-white font-medium">Read article <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" /></span>
            </div>
          </Link>
        )}

        <motion.div variants={stagger} initial="hidden" animate="show" className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rest.map((p) => (
            <motion.div key={p.id} variants={fadeUp}>
              <Link to={`/blog/${p.slug}`} className="group block rounded-2xl overflow-hidden bg-[#121212] border border-white/10 hover:border-[#ccff00]/40 transition-colors h-full" data-testid={`blog-card-${p.slug}`}>
                <div className="h-48 overflow-hidden">
                  <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 text-xs text-zinc-500 mb-3">
                    <span className="text-[#ccff00] uppercase tracking-widest">{p.category}</span>
                    <span className="flex items-center gap-1"><Clock size={12} /> {p.read_time} min</span>
                  </div>
                  <h3 className="font-display font-bold text-xl leading-snug mb-2 group-hover:text-[#ccff00] transition-colors">{p.title}</h3>
                  <p className="text-sm text-zinc-400 line-clamp-3">{p.excerpt}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default BlogList;
